import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';

import { enumCbpAssignedNumberStatus, enumIdentifierQualifier } from '@data-layer/enums/common';
import { identifierEINValidator, identifierSSNValidator } from '@helpers/custom-validator';
import { IItemMask } from '@interfaces/item-mask';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { EnumHelper } from '@helpers/enum.helper';
import { CountryModel } from '@data-layer/models/common';
import { take } from 'rxjs/operators';
import { CodeRepository } from '@data-layer/repositories';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@app/data-layer/models/companies';

@Component({
  selector: 'identifier-qualifier-update',
  templateUrl: './identifier-qualifier-update.component.html',
  styleUrls: ['./identifier-qualifier-update.component.scss'],
})
export class IdentifierQualifierUpdateComponent implements OnInit, OnDestroy {
  @Input() public company: CompanyModel = null;
  @Input() public hide: boolean = false;
  @Input() public isModalPlacement: boolean = false;
  @Input() public formGroup: UntypedFormGroup = null;
  @Input() public errors: IErrorForm = null;
  @Input() public isMobile: boolean = false;
  @Input() public isDisabled: boolean = false;
  @Input() public isOldStyleTheme: boolean = false;
  @Input() public isEditView: boolean = false;
  @Input() public controlId: string = 'identifierControl';

  public identifierControl: AbstractControl;
  public dateOfBirthControl: AbstractControl;
  public identifierQualifierControl: AbstractControl;
  public itemMask: IItemMask = {} as IItemMask;
  public helperIdentifier: string = null;
  public enumIdentifierQualifier: typeof enumIdentifierQualifier = enumIdentifierQualifier;
  public identitiesQual: enumIdentifierQualifier[] = [
    enumIdentifierQualifier.EIN,
    enumIdentifierQualifier.SSN,
    enumIdentifierQualifier.ANI,
  ];
  public isShowDateOfBirth: boolean = false;

  private DEFAULT_MASK: string = '0{13}';
  private maskArray: { [key: string]: IItemMask } = {
    ANI: {
      mask: '000000-00000',
      placeholder: 'YYDDPP-NNNNN',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.ANI',
      maskMaxLength: 11,
    },
    EIN: {
      mask: '00-0000000AA',
      placeholder: 'NN-NNNNNNNAA',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.EIN',
      maskMaxLength: 11,
    },
    SSN: {
      mask: '000-00-0000',
      placeholder: 'NNN-NN-NNNN',
      inputmode: 'numeric',
      maskErrorTooltip: 'MASK_ERROR.ONLY_DIGITS',
      maskMaxLength: 9,
    },
  };
  private defaultCountryCode: string = 'US';
  private defaultCountry: CountryModel;

  private subscription: Subscription[] = [];

  constructor(private cd: ChangeDetectorRef, private companiesService: CompaniesService, private codeRepository: CodeRepository) {}

  public ngOnInit(): void {
    this.setControls();

    this.subscription.push(
      this.identifierQualifierControl.valueChanges.subscribe((val: enumIdentifierQualifier) => {
        this.setIdentifierMaskAndPlaceHolder(val);
      }),
      this.identifierControl.valueChanges.subscribe((val: string) => {
        if (!!val) {
          if (!this.identifierQualifierControl.value) {
            this.identifierQualifierControl.setValidators(Validators.required);
            this.identifierQualifierControl.updateValueAndValidity();
          }
        } else {
          this.identifierQualifierControl.clearValidators();
          this.identifierQualifierControl.updateValueAndValidity();
        }
      }),
    );

    this.setIdentifierMaskAndPlaceHolder(this.identifierQualifierControl.value);

    this.getDefaultCountryAddress()
      .pipe(take(1))
      .subscribe((country) => {
        this.defaultCountry = country;
      });
  }

  public setIdentitiesQual(qual: enumIdentifierQualifier) {
    this.identifierQualifierControl.patchValue(qual);
    this.cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((e) => e.unsubscribe());
  }

  public getDefaultCountryAddress(): Observable<CountryModel> {
    return this.codeRepository.getCountry(this.defaultCountryCode);
  }

  public getIdentifierQualifierTranslationText(qual: enumIdentifierQualifier): string {
    if (qual === enumIdentifierQualifier.ANI && this.isHasAssignedCbpRequest) {
      return 'IDENTIFIER_QUALIFIER.OTHER';
    }

    return 'IDENTIFIER_QUALIFIER.' + qual;
  }

  public get isHasAssignedCbpRequest(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }

  public get isShowAssignedCbpRequestSubmitted(): boolean {
    return this.isHasAssignedCbpRequest && this.isEditView && this.isAssignedCbpRequestSubmitted;
  }

  public get isAssignedCbpRequestSubmittedOrApproved(): boolean {
    return this.isHasAssignedCbpRequest && this.isEditView && (this.isAssignedCbpRequestSubmitted || this.isAssignedCbpRequestApproved);
  }

  public get isAssignedCbpRequestRejected(): boolean {
    return (
      this.isHasAssignedCbpRequest &&
      this.isEditView &&
      this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.REJECTED
    );
  }

  public get isAssignedCbpRequestSubmitted(): boolean {
    return (
      this.isHasAssignedCbpRequest &&
      (this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
        this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING)
    );
  }

  public get isAssignedCbpRequestApproved(): boolean {
    return this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED;
  }

  public get isOtherIdentifierQualifierSelected(): boolean {
    return this.identifierQualifierControl.value === enumIdentifierQualifier.ANI;
  }

  private setIdentifierMaskAndPlaceHolder(valueQual: enumIdentifierQualifier) {
    let validatorFn;

    switch (valueQual) {
      case enumIdentifierQualifier.EIN:
        validatorFn = identifierEINValidator;
        break;
      case enumIdentifierQualifier.SSN:
        validatorFn = identifierSSNValidator;
        break;
      default:
        validatorFn = null;
        break;
    }

    this.setIdentitiesQualDependence(valueQual, validatorFn);

    if (valueQual === enumIdentifierQualifier.SSN) {
      this.isShowDateOfBirth = true;

      this.dateOfBirthControl.updateValueAndValidity({ emitEvent: false });
    } else {
      this.isShowDateOfBirth = false;
      this.dateOfBirthControl.reset();
    }
  }

  private setIdentitiesQualDependence(valueQual: enumIdentifierQualifier, validatorFn?: () => ValidatorFn) {
    const qualName: string = EnumHelper.getEnumKeyByValue(enumIdentifierQualifier, valueQual);
    this.itemMask = this.getItemMask(qualName);
    this.helperIdentifier = !qualName ? null : `FORM_HELPER.IDENTIFIER_${qualName}`;
    if (!validatorFn) {
      this.identifierControl.clearValidators();
      // TODO: MAGIC. WHY. NOT CLEAR
      // this.identifierControl.setValidators(Validators.required);
      this.identifierControl.setValidators(null);
    } else {
      this.identifierControl.setValidators([Validators.required, validatorFn()]);
    }
    this.identifierControl.updateValueAndValidity({ emitEvent: false });
  }

  private setControls(): void {
    this.identifierControl = this.formGroup.get('identifier');
    this.dateOfBirthControl = this.formGroup.get('dateOfBirth');
    this.identifierQualifierControl = this.formGroup.get('identifierQualifier');

    if (this.identifierQualifierControl.disabled) {
      this.dateOfBirthControl.disable();
    }

    if (!this.identifierControl || !this.dateOfBirthControl || !this.identifierQualifierControl) {
      throw new Error(`You can't use this component!`);
    }
  }

  private getItemMask(key: string): IItemMask {
    const { mask = this.DEFAULT_MASK, placeholder = null, inputmode = 'text', maskErrorTooltip, maskMaxLength } = this.maskArray[key] || {};

    return { mask, placeholder, inputmode, maskErrorTooltip, maskMaxLength };
  }
}
